import { ref, onMounted, onUnmounted } from "vue";
import {
  CarPropertyClientClient,
  CarPropertyClientValue,
  SubscribeOptionFlags,
} from "@/utils/capacitor/carPropertyClientPlugin";
import { useDomainControlStore } from "@/stores/domainControl";
import { isDev } from "@/utils/env";

/**
 * HMR / 热更新时闭包丢失但定时器与 CarProperty 订阅可能仍存活；
 * window 上同时挂 timerId 与 cleanup：先 clearInterval 再取消订阅，避免只丢函数引用时定时器仍跑。
 */
const WINDOW_USE_CAR_PROPERTY_HMR = "__x11MediaUseCarPropertyHmr";

type UseCarPropertyHmrRegistration = {
  timerId: ReturnType<typeof setInterval> | null;
  cleanup: () => Promise<void>;
};

type WindowHmrSlot = Record<string, UseCarPropertyHmrRegistration | undefined>;

/** 读取 window 上登记项并清空槽位 */
function clearWindowUseCarPropertyHmr():
  | UseCarPropertyHmrRegistration
  | undefined {
  const w = window as unknown as WindowHmrSlot;
  const reg = w[WINDOW_USE_CAR_PROPERTY_HMR];
  w[WINDOW_USE_CAR_PROPERTY_HMR] = undefined;
  return reg;
}

/** 新实例挂载前：停掉上一实例的 interval，再执行完整 cleanup（含 CarProperty 退订） */
async function disposeStaleUseCarPropertyFromWindow() {
  const reg = clearWindowUseCarPropertyHmr();
  if (!reg) return;
  if (reg.timerId != null) {
    clearInterval(reg.timerId);
    reg.timerId = null;
  }
  await reg.cleanup();
}

/** composable 模块被 Vite HMR 替换时触发；dispose 不宜 await，故 fire-and-forget */
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    const reg = clearWindowUseCarPropertyHmr();
    if (!reg) return;
    if (reg.timerId != null) clearInterval(reg.timerId);
    void reg.cleanup();
  });
}

export interface SignalItem {
  signalName: string;
  displayName: string;
}

export interface UseCarPropertyOptions {
  /** 要订阅的信号列表 */
  subscribeSignals: SignalItem[];
  /** 订阅数据回调，每 50ms 调用一次，传入的 key 为 displayName */
  handleSubscribe: (vehicleProperties: Record<string, any>) => void;
  /** 要发送（回显+set）的信号列表 */
  sendSignals?: SignalItem[];
  /** 回显回调：onMounted 时 getProperty 拉取 sendSignals 后，以 { [displayName]: value } 调用 */
  onOutputEcho?: (keyValue: Record<string, any>) => void;
  /** mock 函数：仅开发环境且开启 store.enableMock 时，每 50ms 将 mock() 的返回值传给 handleSubscribe */
  mock?: () => Record<string, any>;
}

export function useCarProperty(options: UseCarPropertyOptions) {
  const { subscribeSignals, handleSubscribe, sendSignals, onOutputEcho, mock } =
    options;
  const domainControlStore = useDomainControlStore();

  const loading = ref(true);
  const vehicleProperties: Record<string, any> = {};
  const signalNameToDisplayName = new Map<string, string>();

  subscribeSignals.forEach(({ signalName, displayName }) => {
    vehicleProperties[displayName] = null;
    signalNameToDisplayName.set(signalName, displayName);
  });

  const unsubscribeFunctions: (() => Promise<void>)[] = [];
  let timerId: ReturnType<typeof setInterval> | null = null;

  const updateProperty = (signalName: string, value: any) => {
    const displayName = signalNameToDisplayName.get(signalName);
    if (displayName !== undefined) {
      vehicleProperties[displayName] = value;
    }
  };

  const initializeSS4 = async () => {
    console.log(
      "[useCarProperty] 🚀 ~~ initializeSS4 ~~ propertyNames:",
      subscribeSignals
    );
    const propertyNames = subscribeSignals.map((s) => s.signalName);
    if (propertyNames.length === 0) return;
    try {
      const result = await CarPropertyClientClient.getProperty({
        propertyNames,
      });
      if (result && "properties" in result && result.properties) {
        for (const property of result.properties) {
          updateProperty(property.propertyName, property.value);
        }
      }
    } catch (error) {
      console.error("[useCarProperty] SS4 初始化失败:", error);
    }
  };

  const subscribeSS4 = async () => {
    console.log(
      "[useCarProperty] 🚀 ~~ subscribeSS4 ~~ subscribeSignals:",
      subscribeSignals
    );
    const propertyNames = subscribeSignals.map((s) => s.signalName);
    if (propertyNames.length === 0) return;
    try {
      await CarPropertyClientClient.subscribeProperty(
        {
          propertyNames,
          updateRateMs: 10,
          eventFlags:
            SubscribeOptionFlags.UPDATE |
            SubscribeOptionFlags.NEED_CURRENT_VALUE,
        },
        (data: CarPropertyClientValue) => {
          // console.log(
          //   "[useCarProperty] useCarProperty 属性更新:",
          //   data.propertyName,
          //   "=",
          //   JSON.stringify(data.value)
          // );
          updateProperty(data.propertyName, data.value);
        }
      );
      unsubscribeFunctions.push(async () => {
        await CarPropertyClientClient.unsubscribeProperty({ propertyNames });
      });
    } catch (error) {
      console.error("[useCarProperty] SS4 订阅失败:", error);
    }
  };

  const fetchOutputEcho = async () => {
    if (!sendSignals?.length || !onOutputEcho) return;
    try {
      const keyValue = await getOutputValue();
      onOutputEcho(keyValue);
    } catch (error) {
      console.error("[useCarProperty] 回显 getProperty 失败:", error);
    }
  };

  const getOutputValue = async (): Promise<Record<string, any>> => {
    const keyValue: Record<string, any> = {};
    if (!sendSignals?.length) return keyValue;
    try {
      const propertyNames = sendSignals.map((s) => s.signalName);
      const result = await CarPropertyClientClient.getProperty({
        propertyNames,
      });
      if (result && "properties" in result && result.properties) {
        for (const property of result.properties) {
          const item = sendSignals.find(
            (s) => s.signalName === property.propertyName
          );
          if (item) {
            keyValue[item.displayName] = property.value;
          }
        }
      }
    } catch (error) {
      console.error("[useCarProperty] getOutputValue getProperty 失败:", error);
    }
    return keyValue;
  };

  const setOutputValue = async (
    payload: string | Record<string, any>,
    value?: any
  ) => {
    console.log("[useCarProperty] 🚀 ~~ setOutputValue ~~ payload:", payload);
    if (!sendSignals?.length) {
      console.warn("[useCarProperty] 未配置 sendSignals");
      return;
    }

    const entries: [string, any][] =
      typeof payload === "string"
        ? [[payload, value]]
        : Object.entries(payload);

    const propertyNames: string[] = [];
    const values: any[] = [];

    for (const [key, v] of entries) {
      const item = sendSignals.find((s) => s.displayName === key);
      if (!item) {
        console.warn("[useCarProperty] 未找到 displayName:", key);
        continue;
      }
      propertyNames.push(item.signalName);
      values.push(v);
    }

    if (!propertyNames.length) return;

    console.log(
      "[useCarProperty] 🚀 ~~ setOutputValue ~~ propertyNames:",
      propertyNames
    );
    try {
      await CarPropertyClientClient.setProperty({
        propertyNames,
        values,
      });
    } catch (error) {
      console.error("[useCarProperty] setProperty 失败:", error);
    }
  };

  const unsubscribeAll = async () => {
    try {
      for (const unsubscribe of unsubscribeFunctions) {
        await unsubscribe();
      }
      unsubscribeFunctions.length = 0;
    } catch (error) {
      console.error("[useCarProperty] 取消订阅失败:", error);
    }
    if (timerId != null) {
      clearInterval(timerId);
      timerId = null;
    }
  };

  // 取消标志：若 onMounted 异步执行期间组件已卸载，后续步骤全部跳过，
  // 防止 IPC 返回慢时在卸载后创建孤儿 interval / 订阅。
  // onMounted / onUnmounted 同处一个闭包，直接共享此变量，无需引用对象。
  let mountCancelled = false;

  onMounted(async () => {
    try {
      await disposeStaleUseCarPropertyFromWindow();
      if (mountCancelled) return;

      await initializeSS4();
      if (mountCancelled) return;

      await subscribeSS4();
      if (mountCancelled) {
        // subscribeSS4 已建立订阅，需要清理
        await unsubscribeAll();
        return;
      }

      await fetchOutputEcho();
      if (mountCancelled) {
        await unsubscribeAll();
        return;
      }

      if (timerId != null) {
        clearInterval(timerId);
        timerId = null;
      }
      timerId = setInterval(() => {
        if (!domainControlStore.subscribePlaybackPlaying) {
          return;
        }
        let data = { ...vehicleProperties };
        if (domainControlStore.enableMock && mock) {
          data = { ...mock(), ...(window as any).mockData };
        }
        if (domainControlStore.showDebugPanel) {
          domainControlStore.setVehicleProperties(data);
        }
        if (domainControlStore.isRecording) {
          domainControlStore.updateRecordList(data);
        }
        // 打印订阅数据
        if (domainControlStore.printSubscribeData) {
          console.log("[useCarProperty] subscribe data", data);
        }
        handleSubscribe(data);
      }, 50);
      (window as unknown as WindowHmrSlot)[WINDOW_USE_CAR_PROPERTY_HMR] = {
        timerId,
        cleanup: unsubscribeAll,
      };
    } catch (e) {
      console.error("[useCarProperty] onMounted 初始化异常", e);
    } finally {
      loading.value = false;
    }
  });

  onUnmounted(async () => {
    mountCancelled = true;
    await unsubscribeAll();
    const w = window as unknown as WindowHmrSlot;
    const reg = w[WINDOW_USE_CAR_PROPERTY_HMR];
    if (reg?.cleanup === unsubscribeAll) {
      w[WINDOW_USE_CAR_PROPERTY_HMR] = undefined;
    }
  });

  return {
    setOutputValue,
    getOutputValue,
    loading,
  };
}
